import { exportZipUrl } from "../api";

const STORAGE_KEY = "ig_dl_history";
const MAX_ITEMS   = 5;

/** Append an entry; keeps only the most recent MAX_ITEMS. */
export function addHistoryEntry(entry) {
  try {
    const prev = getHistory();
    const next = [entry, ...prev.filter((e) => e.job_id !== entry.job_id)].slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (_) {}
}

/** Read the stored list (safe — returns [] on any error). */
export function getHistory() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch (_) {
    return [];
  }
}

/** Clear the full list. */
export function clearHistory() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
}

/* ------------------------------------------------------------------ */

function statusBadge(status) {
  const map = {
    complete: "bg-green-500/20 text-green-400",
    failed:   "bg-red-500/20 text-red-400",
    queued:       "bg-gray-500/20 text-gray-400",
    downloading:  "bg-blue-500/20 text-blue-400",
    transcribing: "bg-purple-500/20 text-purple-400",
    analyzing:    "bg-amber-500/20 text-amber-400",
  };
  return map[status] ?? "bg-gray-500/20 text-gray-400";
}

function timeAgo(iso) {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 1)  return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch {
    return "";
  }
}

function HistoryRow({ entry, onReopen }) {
  const {
    job_id, url, status, username, content_type,
    files_count = 0, created_at,
  } = entry;

  const displayUrl = url.replace(/^https?:\/\/(www\.)?instagram\.com\//, "").replace(/\/$/, "");

  return (
    <div className="
      group flex items-center gap-4 px-4 py-3
      rounded-xl border border-[#2e2e2e] bg-[#1a1a1a]
      hover:border-indigo-500/40 hover:bg-[#1e1e1e]
      transition-all
    ">
      {/* Left: status dot */}
      <span className={`w-2 h-2 rounded-full shrink-0 ${
        status === "complete" ? "bg-green-500" :
        status === "failed"   ? "bg-red-500"   : "bg-gray-500"
      }`} />

      {/* Centre: url + meta */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-200 truncate">
          {username ? <span className="text-indigo-300">@{username} · </span> : null}
          <span className="text-[#888]">{displayUrl}</span>
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {content_type && (
            <span className="text-xs text-[#555] capitalize">{content_type}</span>
          )}
          {files_count > 0 && (
            <span className="text-xs text-[#555]">· {files_count} file{files_count !== 1 ? "s" : ""}</span>
          )}
          <span className="text-xs text-[#444]">· {timeAgo(created_at)}</span>
        </div>
      </div>

      {/* Right: status badge + actions */}
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(status)}`}>
          {status}
        </span>

        {status === "complete" && (
          <>
            <button
              onClick={() => onReopen(entry)}
              title="View results"
              className="p-1.5 rounded-lg text-[#555] hover:text-white hover:bg-[#2e2e2e] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
            </button>
            <a
              href={exportZipUrl(job_id)}
              download
              title="Download ZIP"
              className="p-1.5 rounded-lg text-[#555] hover:text-white hover:bg-[#2e2e2e] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m-4-4l4 4 4-4"/>
              </svg>
            </a>
          </>
        )}
      </div>
    </div>
  );
}

export default function HistoryPanel({ onReopen }) {
  const history = getHistory();
  if (history.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#555] uppercase tracking-wider">
          Recent downloads
        </h2>
        <button
          onClick={() => { clearHistory(); window.location.reload(); }}
          className="text-xs text-[#444] hover:text-red-400 transition-colors"
        >
          Clear
        </button>
      </div>
      <div className="space-y-2">
        {history.map((entry) => (
          <HistoryRow key={entry.job_id} entry={entry} onReopen={onReopen} />
        ))}
      </div>
    </section>
  );
}
